import React from 'react';
import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
export function SalesLetterSection() {
  return <section className="py-20 px-4 bg-black" data-id="element-127">
      <div className="max-w-2xl mx-auto space-y-16" data-id="element-128">
        {/* APERTURA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-129">
          <h2 className="text-3xl font-bold text-white mb-8" data-id="element-130">
            Si alguna vez intentaste empezar una huerta y te pasó esto…
          </h2>
          <ul className="space-y-4 mb-8" data-id="element-131">
            {['Compraste semillas con ilusión… y no germinaron.', 'Plantaste algo… y se te secó sin entender por qué.', 'Te apareció una plaga… y te arruinó semanas de trabajo.', 'Te entusiasmás un mes… y después se vuelve un desorden.', 'No sabés qué plantar, cuándo, ni cómo sostenerlo todo el año.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-lg" data-id="element-132">
                <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-133" />
                <span data-id="element-134">{item}</span>
              </li>)}
          </ul>
          <p className="text-white font-bold text-xl mb-4" data-id="element-135">
            Entonces entendés lo más frustrante:
          </p>
          <div className="border-l-4 border-green-500 pl-6 py-3 bg-zinc-900/50 italic text-gray-300 text-lg rounded-r-lg" data-id="element-136">
            "No es que 'no se te da'. Es que nadie te dio un sistema simple,
            claro y ordenado."
          </div>
        </motion.div>

        {/* AGITACIÓN */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-137">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-138">
            Porque la huerta no falla por falta de voluntad.
          </h2>
          <p className="text-gray-400 mb-6 text-lg" data-id="element-139">Falla por estas razones:</p>
          <div className="space-y-5" data-id="element-140">
            {[{
            num: '1',
            text: "No hay plan anual. Plantás lo que 'se te ocurre' y después descubrís que era fuera de temporada."
          }, {
            num: '2',
            text: 'No hay método de continuidad. Hacés una siembra y cuando cosechás… se terminó.'
          }, {
            num: '3',
            text: 'No hay control real. Riego, sustrato, plagas y mantenimiento quedan al azar.'
          }].map(item => <div key={item.num} className="flex gap-4 items-start" data-id="element-141">
                <span className="text-green-500 font-black text-2xl leading-none shrink-0 w-8" data-id="element-142">
                  {item.num})
                </span>
                <p className="text-gray-300 text-lg leading-relaxed" data-id="element-143">
                  {item.text}
                </p>
              </div>)}
          </div>
          <p className="text-white font-bold text-lg mt-8" data-id="element-144">
            Y ahí pasa lo típico: la huerta termina siendo un intento más que
            abandonás.
          </p>
        </motion.div>

        {/* REENCUADRE */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-145">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-146">
            El problema no sos vos. Ni tu patio. Ni tu balcón. Ni tu tiempo.
          </h2>
          <p className="text-gray-400 text-lg mb-6" data-id="element-147">
            El problema es que te enseñaron "tips sueltos" en vez de un sistema.
          </p>
          <p className="text-gray-400 text-lg mb-4" data-id="element-148">
            Y sin sistema, la huerta depende de:
          </p>
          <ul className="space-y-2 mb-6" data-id="element-149">
            {['suerte', 'clima', 'prueba y error', 'energía mental constante'].map(item => <li key={item} className="text-gray-500 text-lg pl-4 border-l-2 border-zinc-700" data-id="element-150">
                {item}
              </li>)}
          </ul>
          <p className="text-white font-bold text-xl" data-id="element-151">Eso no es sostenible.</p>
        </motion.div>

        {/* DESCUBRIMIENTO */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 relative overflow-hidden" data-id="element-152">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-green-700" data-id="element-153" />
          <p className="text-green-500 font-bold uppercase tracking-widest text-sm mb-3" data-id="element-154">
            El Descubrimiento
          </p>
          <h3 className="text-2xl font-bold text-white mb-4" data-id="element-155">
            La huerta se vuelve fácil cuando se convierte en una rutina
            organizada.
          </h3>
          <p className="text-gray-400 mb-6" data-id="element-156">
            Y esa rutina se sostiene con 3 pilares:
          </p>
          <ul className="space-y-4" data-id="element-157">
            {[{
            label: 'Pilar 1 — Planificación real',
            desc: 'Qué plantar, cuándo y en qué orden, para cosechar sin cortes.'
          }, {
            label: 'Pilar 2 — Producción continua',
            desc: 'Siembra escalonada + rotación + asociaciones correctas.'
          }, {
            label: 'Pilar 3 — Control y mantenimiento simple',
            desc: 'Riego correcto, prevención de plagas y ajustes fáciles sin complicarte.'
          }].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-158">
                <div className="bg-green-500/20 p-1 rounded-full shrink-0 mt-0.5" data-id="element-159">
                  <Check className="w-4 h-4 text-green-500" data-id="element-160" />
                </div>
                <div data-id="element-161">
                  <span className="text-white font-bold" data-id="element-162">{item.label}: </span>
                  <span className="text-gray-400" data-id="element-163">{item.desc}</span>
                </div>
              </li>)}
          </ul>
          <p className="text-white font-bold text-lg mt-8" data-id="element-164">
            Cuando esos 3 pilares están, tu huerta deja de ser un hobby
            improvisado y se convierte en un sistema que produce.
          </p>
        </motion.div>

        {/* PRESENTACIÓN DEL PRODUCTO */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-165">
          <p className="text-gray-400 text-lg mb-4" data-id="element-166">Por eso existe:</p>
          <h2 className="text-4xl md:text-5xl font-black text-green-500 mb-2" data-id="element-167">
            HUERTA PRODUCTIVA 360°
          </h2>
          <p className="text-xl text-white font-semibold mb-8" data-id="element-168">
            Sistema Integral desde Cero
          </p>
          <p className="text-gray-400 text-lg mb-6 text-left" data-id="element-169">
            Un pack digital diseñado para que puedas:
          </p>
          <ul className="space-y-3 text-left" data-id="element-170">
            {['empezar desde cero (sin experiencia)', 'organizar tu espacio de forma inteligente', 'producir alimentos todo el año', 'reducir gastos de verdulería', 'y si querés, escalar para vender o emprender'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300 text-lg" data-id="element-171">
                <Check className="w-5 h-5 text-green-500 shrink-0" data-id="element-172" />
                <span data-id="element-173">{item}</span>
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}